import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { ExtendedTourPackageStore, ITourPackage } from '../types/types';
import { createTour, deleteTour, getTourById, getTours, updateTour } from '../utils/api';

const useTourPackage: UseBoundStore<StoreApi<ExtendedTourPackageStore>> = create((set) => ({
  tours: [],
  detailTour: null,
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  page: 1,
  hasMore: true,
  meta: null,
  isLoadingNextPage: false,
  hasFetched: false,

  fetchTours: async (page = null, reset = false) => {
    try {
      set({ ...(reset ? { fetchLoading: true } : { isLoadingNextPage: true }) });
      const { success, message, data, meta } = await getTours(page, page ? 15 : null);
      if (!success) throw new Error(message);

      set((state) => {
        const newData = page
          ? data.filter((item: ITourPackage) => !state.tours.some((p) => p.tour_id === item.tour_id))
          : data;

        let updatedTours;
        if (reset) {
          updatedTours = data;
        } else if (page) {
          updatedTours = [...state.tours, ...newData];
        } else {
          updatedTours = data;
        }
        const isPaginated = meta?.paginated ?? false;
        const totalPages = meta?.totalPages ?? 1;
        const currentPage = page || 1;
        const hasMore = isPaginated ? currentPage < totalPages : false;

        return {
          tours: updatedTours,
          page: currentPage,
          hasMore: data.length > 0 && hasMore,
          meta: meta ?? null,
          hasFetched: true,
        };
      });
      return { message, success, data };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false, isLoadingNextPage: false });
    }
  },

  fetchTourById: async (id: number) => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getTourById(id);
      if (!success) throw new Error(message);
      set({ detailTour: data });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      set({ detailTour: undefined });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  addTour: async (tourData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createTour(tourData);
      if (!success) throw new Error(message);
      set((state) => ({
        tours: state.tours
          ? [...state.tours, data].sort(
              (a: any, b: any) => new Date(b.edited_at).getTime() - new Date(a.edited_at).getTime(),
            )
          : [data],
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editTour: async (id, tourData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateTour(id, tourData);
      if (!success) throw new Error(message);
      set((state) => ({
        tours: state.tours
          ?.map((tour) => (tour.tour_id === id ? data : tour))
          .sort((a: any, b: any) => new Date(b.edited_at).getTime() - new Date(a.edited_at).getTime()),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeTour: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteTour(id);
      if (!success) throw new Error(message);
      set((state) => ({
        tours: state.tours?.filter((tour) => tour.tour_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useTourPackage;
